<script setup lang="ts">
import { formatPrice } from '@/event/locale'
import { configDeliveryZones } from '@/storage/setting/configDeliveryZones'
import { ref, computed, onUnmounted } from 'vue'

const props = defineProps<{
    options: any[]
    // modelValue?: string | number | null
    displayKey?: string
    valueKey?: string | string[]
    placeholder?: string
    disabled?: boolean
    error?: boolean
    theme?: 'happy' | 'default' | 'admin'
    selected: any
}>()

const emit = defineEmits(['update:modelValue', 'change', 'sendError'])
const open = ref(false)
const isPlaceholder = ref(true)
const selectDisabledValue = ref(false)

const selectedValue = computed(() => {
    if (props.selected && props.valueKey === 'zone') {
        isPlaceholder.value = false
        const currentValue = configDeliveryZones.value.find(
            (item) => item.zone_id === props.selected.zone_id
        )

        return currentValue
            ? getItemDisplay(currentValue)
            : props.placeholder || 'Sélectionner...'
    }

    if (props.selected && props.valueKey !== 'zone') {
        isPlaceholder.value = false
        const currentValue = getItemDisplay(props.selected)
        return currentValue ? currentValue : props.placeholder || 'Sélectionner...'
    }

    isPlaceholder.value = false

    return props.placeholder || 'Sélectionner...'
})

const placeholderText = computed(() => {
    return props.placeholder || 'Sélectionner...'
})

const selectClasses = computed(() => {
    const theme = props.theme || 'default'
    const classes = ['custom-select', `custom-select--${theme}`]

    if (open.value && !props.disabled) {
        classes.push(`custom-select--open-${theme}`)
    }
    if (props.disabled) {
        classes.push(`custom-select--disabled-${theme}`)
    }
    if (props.error || selectDisabledValue.value) {
        classes.push(`custom-select--error-${theme}`)
    }
    return classes
})

const errorStyle = computed(() => {
    let error: boolean = false

    if (props.error) {
        error = true
    }

    if (selectDisabledValue.value) {
        error = true
    }

    return error
})

function getItemDisplay(item: any): string {
    if (Array.isArray(props.displayKey)) {
        return props.displayKey.map((key) => item[key]).join(' - ')
    }
    if (typeof props.displayKey === 'object') return item
    if (props.displayKey) return item[props.displayKey]
    if (typeof item === 'string' || typeof item === 'number') return String(item)
    return item.label || item.name || item.zone || String(item)
}

function getItemValue(item: any): string | number {
    if (Array.isArray(props.valueKey)) {
        return props.valueKey.map((key) => item[key]).join('-')
    }

    if (typeof props.valueKey === 'object') return item
    if (props.valueKey && item[props.valueKey]) return item[props.valueKey]

    if (typeof item === 'string' || typeof item === 'number') return item
    return item.value || item.id || item.city_id || item.zone || item
}

function toggle() {
    if (props.disabled) {
        emit('sendError', true)
        return
    }
    open.value = !open.value
}

function select(item: any) {
    const value = getItemValue(item)
    emit('update:modelValue', value)
    emit('change', item)
    open.value = false
}

function selectWrong() {
    selectDisabledValue.value = true
    setTimeout(() => {
        selectDisabledValue.value = false
    }, 2000)
}

function onClickOutside(event: MouseEvent) {
    const target = event.target as Element
    if (target && !target.closest('.custom-select')) {
        open.value = false
    }
}

window.addEventListener('click', onClickOutside)

onUnmounted(() => {
    window.removeEventListener('click', onClickOutside)
})
</script>

<template>
    <div :class="[selectClasses, { shake: errorStyle }]" @click="toggle">
        <div class="selected">
            <span
                class="selected-text"
                :class="{
                    'selected-text--placeholder': isPlaceholder
                }"
                >{{ selectedValue }}</span
            >
            <span class="arrow" :class="{ 'arrow--rotated': open }">▼</span>
        </div>

        <ul
            v-if="open && !disabled"
            class="options"
            :class="{ 'option--error': errorStyle }"
        >
            <li
                class="option"
                @click.stop="select('')"
                :class="{
                    'border-b border-border-gray-happy-light ': options.length > 0
                }"
            >
                {{ placeholderText }}
            </li>
            <li
                v-for="(item, index) in options"
                :key="index"
                class="option"
                :class="{
                    'option--selected':
                        getItemValue(item) === getItemValue(props.selected),
                    'option--disabled': item.disabled === true,
                    'border-b border-border-gray-happy-light ': index < options.length - 1
                }"
                @click.stop="item.disabled ? selectWrong() : select(item)"
            >
                <span class="" :class="{ 'line-through': item.disabled }">
                    {{ getItemDisplay(item) }}</span
                >
                <span
                    v-if="item.disabled"
                    class="no-underline text-border-red-happy-light font-semibold"
                >
                    {{ 'Commande minimum : ' + formatPrice(item.minimum_price) }}
                </span>
            </li>
        </ul>
    </div>
</template>

<style scoped>
.custom-select {
    @apply relative w-full cursor-pointer;
}

.custom-select--disabled-default {
    @apply opacity-50 cursor-not-allowed;
}

.custom-select--error-default .selected {
    @apply border-red-500 flex items-center;
}

.selected {
    /* refactored */
    @apply flex items-center flex-wrap justify-between px-2 py-1 min-h-8 border-2 rounded-md  transition-colors;
    @apply border-t-border bg-t-card;
    /* ifo-dark: pill tarzı — yumuşak amber border + dark-transparan bg. */
    @apply happy-ifo-dark:rounded-full happy-ifo-dark:border happy-ifo-dark:border-t-accent/30 happy-ifo-dark:bg-t-accent/5 happy-ifo-dark:px-4 happy-ifo-dark:shadow-none;
}

.custom-select--open-happy .options {
    @apply absolute z-30;
}

.custom-select--open-default .selected {
    @apply border-blue-500;
}

.selected-text {
    /* refactored */
    @apply flex items-center justify-center text-center flex-1  text-t-text;
}

.selected-text--placeholder {
    /* refactored */
    @apply text-t-text-muted;
}

.arrow {
    @apply transition-transform duration-200;
}

.arrow--rotated {
    @apply rotate-180;
}

.options {
    /* refactored */
    @apply absolute top-full left-0 right-0 mt-1 border-2  rounded-md shadow-lg  max-h-60 overflow-y-auto;
    @apply bg-t-card border-t-border;
    /* ifo-dark: dropdown için yumuşatılmış köşeler + amber border. */
    @apply happy-ifo-dark:rounded-xl happy-ifo-dark:border happy-ifo-dark:border-t-accent/30 happy-ifo-dark:bg-t-card happy-ifo-dark:shadow-[0_8px_24px_-8px_rgba(0,0,0,0.6)];
}
.options.option--error {
    @apply border-red-500;
}

.option {
    @apply px-2 py-1 min-h-8 flex flex-col items-center text-center justify-center  cursor-pointer transition-colors;
    @apply happy-ifo-dark:text-t-text;
    /* ifo-dark: option'lar arası çizgi — template'te border-b border-border-gray-happy-light
       veriliyordu, ifo-dark temasında gri hatlar sırıtıyordu. Amber/10 ile yumuşatıldı. */
    @apply happy-ifo-dark:!border-t-accent/20;
}

.option--selected {
    /* refactored */
    @apply bg-t-accent/25 text-t-accent flex;
    @apply happy-urban-dark-red:bg-urban-red/25 happy-urban-dark-red:text-urban-dark;
    @apply happy-urban-light-red:bg-urban-red/25 happy-urban-light-red:text-urban-dark;
    @apply happy-orange-light:bg-bg-orange/25 happy-orange-light:text-urban-dark;
    @apply happy-orange-dark:bg-bg-orange/25 happy-orange-dark:text-urban-dark;
    @apply happy-fresh-mint:bg-bg-mint/25 happy-fresh-mint:text-urban-dark;
    @apply happy-bee-light:bg-bee-yellow/25 happy-bee-light:text-urban-dark;
    @apply happy-bee-dark:bg-bee-yellow/25 happy-bee-dark:text-urban-dark;
    @apply happy-ifo-dark:bg-t-accent/25 happy-ifo-dark:text-t-text;
    @apply happy-earth-stone:bg-earth-card-dark/25 happy-earth-stone:text-urban-dark;
    @apply happy-scorched-earth:bg-scorched-earth-red/25 happy-scorched-earth:text-urban-dark;
    @apply happy-sand-sky:bg-sand-blue-dark/25 happy-sand-sky:text-urban-dark;
    @apply happy-gray-sand:bg-bg-sand/25 happy-gray-sand:text-urban-dark;
    @apply happy-purple-gradient:bg-pink-light/35 happy-purple-gradient:text-urban-dark;
}

.option--disabled {
    @apply bg-banner-gray-component-happy-light;
}

@media (hover: hover) {
    .option:hover {
        /* refactored */
        @apply bg-t-accent/15;
        @apply happy-urban-dark-red:bg-urban-red/25 happy-urban-dark-red:text-urban-dark;
        @apply happy-urban-light-red:bg-urban-red/25 happy-urban-light-red:text-urban-dark;
        @apply happy-orange-light:bg-bg-orange/25 happy-orange-light:text-urban-dark;
        @apply happy-orange-dark:bg-bg-orange/25 happy-orange-dark:text-urban-dark;
        @apply happy-fresh-mint:bg-bg-mint/25 happy-fresh-mint:text-urban-dark;
        @apply happy-bee-light:bg-bee-yellow/25 happy-bee-light:text-urban-dark;
        @apply happy-bee-dark:bg-bee-yellow/25 happy-bee-dark:text-urban-dark;
        @apply happy-ifo-dark:bg-t-accent/25 happy-ifo-dark:text-t-text;
        @apply happy-earth-stone:bg-earth-card-dark/25 happy-earth-stone:text-urban-dark;
        @apply happy-scorched-earth:bg-scorched-earth-red/25 happy-scorched-earth:text-urban-dark;
        @apply happy-sand-sky:bg-sand-blue-dark/25 happy-sand-sky:text-urban-dark;
        @apply happy-gray-sand:bg-bg-sand/25 happy-gray-sand:text-urban-dark;
        @apply happy-purple-gradient:bg-pink-light/35 happy-purple-gradient:text-urban-dark;
    }
}

.error-message {
    @apply text-red-500 text-sm mt-1;
}

/** Theme Happy */

.custom-select--happy {
    @apply text-text-dark-happy-light;
    @apply happy-dark:bg-text-dark-happy-light;
    @apply happy-light:bg-bg-happy-light;
    /* ifo-dark: dark card bg + dark base text = unreadable. Flip to light text. */
    @apply happy-ifo-dark:text-t-text;
}
.custom-select--open-happy .selected {
    @apply border-button-yellow-happy-light outline-none;
    @apply happy-dark:border-bee-yellow;
    @apply happy-urban-dark-red:border-urban-red;
    @apply happy-urban-light-red:border-urban-red;
    @apply happy-orange-light:border-bg-orange;
    @apply happy-orange-dark:border-bg-orange;
    @apply happy-fresh-mint:border-bg-mint;
    @apply happy-bee-light:border-bee-yellow;
    @apply happy-bee-dark:border-bee-yellow;
    @apply happy-ifo-dark:border-t-accent;
    @apply happy-earth-stone:border-earth-card-dark;
    @apply happy-scorched-earth:border-scorched-earth-red;
    @apply happy-sand-sky:border-sand-blue-dark;
    @apply happy-gray-sand:border-gray-sand-gray-dark;
    @apply happy-purple-gradient:border-pink-light;
}
.custom-select--disabled-happy {
    @apply text-opacity-50 cursor-not-allowed;
}
.custom-select--error-happy .selected {
    @apply border-border-red-happy-light;
}

/*   'custom-select',
    `custom-select--${props.theme || 'default'}`,
    {
        [`custom-select--open-${props.theme || 'default'}`]: open.value,
        [`custom-select--disabled-${props.theme || 'default'}`]: props.disabled,
        [`custom-select--error-${props.theme || 'default'}`]: props.error
    } */

@keyframes shake {
    0% {
        transform: rotate(0deg);
    }
    15% {
        transform: rotate(1deg);
    }
    30% {
        transform: rotate(0deg);
    }
    45% {
        transform: rotate(-1deg);
    }
    60% {
        transform: rotate(0deg);
    }
    75% {
        transform: rotate(-1deg);
    }
    100% {
        transform: rotate(0deg);
    }
}
.shake {
    animation: shake 0.5s ease-in-out;
    @apply relative z-30;
}
</style>
